/**
 * Phase B — Admin settings: batch toggle 3 단계.
 *
 * - batchVision: Gemini Batch API 사용 (OCR 단계). production verified, 50% 절감.
 * - batchReportExtract: report_extract batch (미래, 아직 noop).
 *
 * (batchFanout 토글 제거 — fanOut 단계가 위키 단순화 step4 에서 삭제. cron 재계산이 대체.)
 */
import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { ErrorBox } from './Watchlist';

export default function AdminSettingsPage() {
  const [reloadKey, setReloadKey] = useState(0);
  const { data, error, loading } = useAsync(() => api.settings(), [reloadKey]);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (error) return <ErrorBox msg={error} />;
  if (loading || !data) return <p className="text-slate-500">로딩 중…</p>;

  const toggle = async (
    key: 'batchVision' | 'batchReportExtract',
    next: boolean,
  ) => {
    setSavingKey(key);
    setErrorMsg(null);
    try {
      await api.patchSettings({ [key]: next });
      setReloadKey((k) => k + 1);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingKey(null);
    }
  };

  // 현재 실제 cost/latency 에 영향 주는 건 vision batch 뿐(report_extract 는 미구현 noop) → 상태는 vision 기준.

  return (
    <section>
      <h1 className="text-2xl font-semibold tracking-tight">⚙️ Admin Settings</h1>
      <p className="mt-1 text-sm text-slate-500">
        Gemini Batch API 단계별 toggle. 현재 vision OCR batch 만 실동작(절감, latency 수~수십 분 추가).
      </p>

      {errorMsg && (
        <div className="mt-3">
          <ErrorBox msg={errorMsg} />
        </div>
      )}

      <div className="mt-6 space-y-3">
        <SettingRow
          label="📄 Tier1 Vision (OCR) batch"
          description="PDF → markdown OCR. Gemini Batch API, AI Gateway path. production verified."
          value={data.batchVision}
          saving={savingKey === 'batchVision'}
          onToggle={(v) => toggle('batchVision', v)}
          status="active"
        />
        <SettingRow
          label="🧠 Tier2 Report Extract batch"
          description="markdown → JSON (target_price, recommendation, summary). 추후 구현."
          value={data.batchReportExtract}
          saving={savingKey === 'batchReportExtract'}
          onToggle={(v) => toggle('batchReportExtract', v)}
          status="future"
        />
      </div>

      <div className="mt-6 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
        <p className="font-medium text-slate-700">현재 상태</p>
        <p className="mt-1 text-slate-600">
          {!data.batchVision && '🟢 vision 실시간 처리 (정상 default)'}
          {data.batchVision && '🟡 vision OCR batch ON — cost 절감, latency 수~수십 분+'}
        </p>
        <p className="mt-2 text-xs text-slate-500">
          ⚠️ 현재 실제 동작은 vision batch 뿐(report_extract 는 미구현 noop). vision 만 ON 시
          cost 절감 작고 latency 추가 → 비추천.
        </p>
      </div>
    </section>
  );
}

function SettingRow({
  label,
  description,
  value,
  saving,
  onToggle,
  status,
}: {
  label: string;
  description: string;
  value: boolean;
  saving: boolean;
  onToggle: (next: boolean) => void;
  status: 'active' | 'future';
}) {
  return (
    <div className="flex items-start justify-between rounded-lg border border-slate-200 bg-white px-4 py-3">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <p className="font-medium text-slate-900">{label}</p>
          {status === 'future' && (
            <span className="pill bg-amber-100 text-amber-700 text-xs">미구현</span>
          )}
        </div>
        <p className="mt-1 text-sm text-slate-500">{description}</p>
      </div>
      <button
        onClick={() => onToggle(!value)}
        disabled={saving}
        className={`ml-4 flex h-6 w-12 items-center rounded-full p-0.5 transition-colors ${
          value ? 'bg-indigo-600' : 'bg-slate-300'
        } disabled:opacity-50`}
        title={value ? 'ON — 변경하려면 클릭' : 'OFF — 변경하려면 클릭'}
      >
        <div
          className={`h-5 w-5 transform rounded-full bg-white transition-transform ${
            value ? 'translate-x-6' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}
